import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronRight, Filter, Loader } from "lucide-react";
import { setTopNavNameTitle } from "../../../redux/features/mobileLayout";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../redux/store";
import { getManagerEmployees } from "../../../redux/features/timesheet/api";
import { getPayperiod } from "../../../redux/features/payperiod/api";
import { NgwAvatar } from "../../common";
import { setGlobalBackHandler, clearGlobalBackHandler } from "../../../hooks";
import { useNavigationHistory } from "../../../hooks/useNavigationHistory";
import { convertDecimalHoursToHM } from "../../../utils/employeeTimeCardhelper";
import { EmployeeSearchResult } from "../../../types/response";
import Select from "react-select";
import { buildPayperiodOptions, resolveAutoSelectedPayperiod } from "../../../utils/payPeriodSelection";

const ManagerTimesheetList: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { setSourcePath } = useNavigationHistory();

    // Get data from Redux store
    const { employeeRecords, isFetchingEmployees } = useSelector((state: RootState) => state.timesheet);
    const user = useSelector((state: RootState) => state.user.user);
    const { payperiodFilters } = useSelector((state: RootState) => state.payperiod);

    // Local state for filters and selected pay period
    const localStoragePayperiod = localStorage.getItem("selectedManagerPayperiod") || "";
    const [selectedPayperiod, setSelectedPayperiod] = useState<string>(localStoragePayperiod);
    const [selectedStartDate, setSelectedStartDate] = useState<string>("");
    const [selectedEndDate, setSelectedEndDate] = useState<string>("");
    const [loadMoreCount, setLoadMoreCount] = useState<number>(-5);
    const [payperiodOptions, setPayperiodOptions] = useState<any[]>([]);
    const [loadingPayperiod, setLoadingPayperiod] = useState<boolean>(false);
    const prevStartDateRef = React.useRef<string>("");
    const prevEndDateRef = React.useRef<string>("");

    // Get saved filters from localStorage or use defaults
    const defaultFilters = {
        departmentCodes: [] as string[],
        groupCodes: [] as string[],
        siteCodes: [] as string[],
        fields: ["total_hours", "total_overtime", "doubletime_hours", "total_break_minutes"] as string[],
        selectedEmployees: [] as EmployeeSearchResult[],
    };

    const [filters, setFilters] = useState(() => {
        const savedFilters = localStorage.getItem("managerTimesheetFilters");
        return savedFilters ? JSON.parse(savedFilters) : defaultFilters;
    });

    useEffect(() => {
        dispatch(setTopNavNameTitle("Manager Timesheet"));
        
        // Remember where we came from (likely /mobile-dashboard)
        if (location.state?.from) {
            setSourcePath(location.state.from);
        } else {
            setSourcePath("/mobile-dashboard");
        }
        
        // Set the back handler to go to the source path
        setGlobalBackHandler(() => {
            // Navigate back to mobile dashboard or previous screen
            navigate("/mobile-dashboard");
            return true;
        });
        
        // Clean up when component unmounts
        return () => {
            clearGlobalBackHandler();
        };
    }, [dispatch, location.state, navigate, setSourcePath]);

    // Load payperiods when component mounts
    useEffect(() => {
        if (user?.os_emp_account_id && !payperiodFilters?.length) {
            dispatch(getPayperiod({ employee_id: user?.os_emp_account_id, count: loadMoreCount }));
        }
    }, [dispatch, user?.os_emp_account_id, loadMoreCount, payperiodFilters?.length]);

    // Handle payperiod options updates
    useEffect(() => {
        if (payperiodFilters?.length) {
            const newPayperiodOptions = buildPayperiodOptions(payperiodFilters);
            const resolvedPayperiod = resolveAutoSelectedPayperiod({
                payperiodOptions: newPayperiodOptions,
                locationState: location.state as
                    | { payPeriod?: string; payPeriodStart?: string; payPeriodEnd?: string; targetDate?: string }
                    | null,
                storageKey: "selectedManagerPayperiod",
                storageDateKey: "selectedManagerPayperiodDate",
            });

            if (resolvedPayperiod) {
                setSelectedPayperiod(resolvedPayperiod);
                localStorage.setItem("selectedManagerPayperiod", resolvedPayperiod);
                const [startDatetime, endDatetime] = resolvedPayperiod.split(" - ");
                handlePayperiodChange(startDatetime, endDatetime);
            }

            setPayperiodOptions([...newPayperiodOptions, { value: "load-more", label: "Load More" }]);
        }
    }, [location.state, payperiodFilters]);

    // Load employees when filters or pay period change
    useEffect(() => {
        loadEmployees();
    }, [filters, selectedPayperiod]);

    const loadEmployees = () => {
        // Extract employee IDs from selected employees
        const selectedEmployeeIds = filters.selectedEmployees?.map((emp: EmployeeSearchResult) => emp.os_emp_account_id) || [];
        
        // Create request payload
        const payload = {
            pay_period: selectedPayperiod,
            employee_id: selectedEmployeeIds, // Array of selected employee IDs
            department_code: filters.departmentCodes,
            group_code: filters.groupCodes,
            site_code: filters.siteCodes,
            fields: filters.fields,
            name_search: filters.name,
        };

        // Call API to fetch employees
        dispatch(getManagerEmployees(payload)).catch((error) => {
            console.error("Error fetching manager employee data:", error);
        });
    };

    const handleEmployeeSelect = (employeeId: string) => {
        // Set the current path as source for the employee detail page
        // This ensures proper back navigation when returning from employee detail
        navigate(`/m-manager-timesheet/${employeeId}`, {
            state: {
                from: "/m-manager-timesheet",
                payPeriod: selectedPayperiod,
                payPeriodStart: selectedStartDate,
                payPeriodEnd: selectedEndDate,
            }
        });
    };

    const handleLoadMore = () => {
        if (user?.os_emp_account_id) {
            const newCount = loadMoreCount - 5;
            setLoadMoreCount(newCount);
            setLoadingPayperiod(true);
            dispatch(getPayperiod({ employee_id: user?.os_emp_account_id, count: newCount })).finally(() => {
                setLoadingPayperiod(false);
            });
        }
    };

    const handlePayperiodChange = (startDate: string, endDate: string) => {
        // Avoid unnecessary API calls if the dates are the same
        if (startDate === prevStartDateRef.current && endDate === prevEndDateRef.current) {
            return;
        }

        setSelectedStartDate(startDate);
        setSelectedEndDate(endDate);

        prevStartDateRef.current = startDate;
        prevEndDateRef.current = endDate;

        // Extract employee IDs from selected employees
        const selectedEmployeeIds = filters.selectedEmployees?.map((emp: EmployeeSearchResult) => emp.os_emp_account_id) || [];

        // Create request payload with the new dates
        const payload = {
            pay_period: `${startDate} - ${endDate}`,
            employee_id: selectedEmployeeIds, // Array of selected employee IDs
            department_code: filters.departmentCodes,
            group_code: filters.groupCodes,
            site_code: filters.siteCodes,
            fields: filters.fields,
            name_search: filters.name,
        };

        // Call API to fetch employees with new dates
        dispatch(getManagerEmployees(payload)).catch((error) => {
            console.error("Error fetching manager employee data:", error);
        });
    };

    const handlePayperiodDropdownChange = (selectedOption: any) => {
        if (selectedOption.value === "load-more") {
            handleLoadMore();
        } else {
            setSelectedPayperiod(selectedOption?.value);
            localStorage.setItem("selectedManagerPayperiod", selectedOption?.value);
            const [startDatetime, endDatetime] = selectedOption.value.split(" - ");
            handlePayperiodChange(startDatetime, endDatetime);
        }
    };

    const navigateToFilters = () => {
        setGlobalBackHandler(() => {
            // Navigate back to the previous screen
            navigate("/m-manager-timesheet");
        });
        navigate("/m-manager-timesheet-filters", { state: filters });
    };

    // Get any active filters count for display
    const getActiveFiltersCount = (): number => {
        let count = 0;
        if (filters.departmentCodes.length) count++;
        if (filters.groupCodes.length) count++;
        if (filters.siteCodes.length) count++;
        if (filters.selectedEmployees?.length) count++;
        return count;
    };

    return (
        <div className="container mx-auto bg-white h-full flex flex-col">
            <div className="flex flex-col text-gray-800 text-base font-sans h-full">
                <div className="px-4 py-3 flex flex-col h-full">
                    <div className="flex flex-col">
                        <div className="mb-3">
                            <Select
                                value={payperiodOptions?.find((option) => option?.value === selectedPayperiod)}
                                onChange={handlePayperiodDropdownChange}
                                options={payperiodOptions}
                                className="text-sm"
                                classNamePrefix="react-select"
                                placeholder="Select Pay Period"
                            />
                        </div>

                        {loadingPayperiod && (
                            <div className="flex justify-center items-center py-4">
                                <Loader
                                    size={32}
                                    color="#2664eb"
                                    strokeWidth={2.5}
                                    absoluteStrokeWidth
                                    className="animate-spin"
                                />
                            </div>
                        )}

                        <button
                            onClick={navigateToFilters}
                            className="w-full py-2 px-4 border border-gray-300 rounded-md flex justify-between items-center mb-4"
                        >
                            <div className="flex items-center">
                                <Filter size={18} className="mr-2 text-gray-600" />
                                <span>Filters</span>
                                {getActiveFiltersCount() > 0 && (
                                    <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-xs">
                                        {getActiveFiltersCount()}
                                    </span>
                                )}
                            </div>
                            <ChevronRight size={18} className="text-gray-400" />
                        </button>
                    </div>

                    <div className="overflow-y-auto flex-1">
                        {isFetchingEmployees ? (
                            <div className="flex justify-center items-center py-10">
                                <div className="h-6 w-6 border-2 border-t-transparent border-blue-500 rounded-full animate-spin"></div>
                            </div>
                        ) : (
                            <>
                                {employeeRecords && employeeRecords.length > 0 ? (
                                    <div className="bg-white rounded-md shadow-sm pb-16">
                                        {employeeRecords.map((employee) => {
                                            const name = employee.emp_detail?.emp_name;
                                            const firstName = name ? name.split(" ")[0] : "";
                                            const lastName = name ? name.split(" ")[1] : "";
                                            return (
                                                <div
                                                    key={employee.emp_detail?.employee_id}
                                                    onClick={() =>
                                                        handleEmployeeSelect(employee.emp_detail?.employee_id || "")
                                                    }
                                                    className="flex items-center justify-between p-4 border-b border-gray-100 cursor-pointer"
                                                >
                                                    <div className="flex items-center">
                                                        <div className="w-10 h-10 mr-3 overflow-hidden rounded-full bg-gray-200 flex-shrink-0">
                                                            {employee.emp_detail?.profile_image_url ? (
                                                                <img
                                                                    src={employee.emp_detail.profile_image_url}
                                                                    alt={`${firstName} ${lastName}`}
                                                                    className="w-full h-full object-cover"
                                                                />
                                                            ) : (
                                                                <NgwAvatar name={employee?.emp_detail?.emp_name} />
                                                            )}
                                                        </div>
                                                        <div>
                                                            <div className="font-medium">
                                                                {firstName} {lastName}
                                                            </div>
                                                            {/* <div className="text-sm text-gray-500">
                                                                {employee.department?.name || "No dept"}
                                                            </div> */}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center">
                                                        <div className="mr-2 font-medium">
                                                            {employee.total_hours}
                                                        </div>
                                                        <ChevronRight size={18} className="text-gray-400" />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="text-center py-10 text-gray-500">
                                        No employee data found. Try changing your filters.
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ManagerTimesheetList;
